import React, {useEffect, useState, useRef} from 'react';
import {
    Alert,
    alpha,
    Box,
    Button,
    Container,
    createTheme,
    Divider,
    Fade,
    FormControl,
    FormControlLabel,
    FormGroup,
    FormLabel,
    Grid,
    Grow,
    IconButton,
    List,
    ListItem,
    ListItemText,
    MenuItem,
    Paper,
    Select,
    Snackbar,
    Switch,
    TextField,
    ThemeProvider,
    Tooltip,
    Typography,
    Zoom,
} from '@mui/material';
import {
    Add as AddIcon,
    Delete as DeleteIcon,
    Schedule as ScheduleIcon,
    NotificationsActive as BellIcon,
    PlayArrow as PlayIcon,
    AlarmOn as AlarmIcon,
    MusicNote as MusicNoteIcon
} from '@mui/icons-material';
import {useDispatch, useSelector} from 'react-redux';
import {createPeriod, deletePeriod, fetchPeriods,updatePeriods} from './redux/periodActions';
import {selectSchoolDetails} from '../../../../common';
import {styled} from '@mui/material/styles';
import {motion} from 'framer-motion';
import useBellSound from './useBellSound';
import bellSounds from './bellSoundsLocal';

// Modern theme configuration
const theme = createTheme({
    palette: {
        primary: {
            main: '#2563eb',
            light: '#60a5fa',
            dark: '#1d4ed8',
        },
        secondary: {
            main: '#7c3aed',
            light: '#a78bfa',
            dark: '#6d28d9',
        },
        background: {
            default: '#f8fafc',
            paper: '#ffffff',
        },
    },
    typography: {
        fontFamily: '"Plus Jakarta Sans", "Inter", "Roboto", sans-serif',
        h4: {
            fontWeight: 800,
            letterSpacing: '-0.5px',
        },
        h6: {
            fontWeight: 600,
            letterSpacing: '-0.3px',
        },
    },
    components: {
        MuiButton: {
            styleOverrides: {
                root: {
                    borderRadius: 12,
                    textTransform: 'none',
                    fontWeight: 600,
                    padding: '12px 24px',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: '0 8px 20px rgba(0, 0, 0, 0.12)',
                    },
                },
            },
        },
        MuiTextField: {
            styleOverrides: {
                root: {
                    '& .MuiOutlinedInput-root': {
                        borderRadius: 12,
                        transition: 'all 0.2s ease-in-out',
                        '&:hover': {
                            transform: 'translateY(-1px)',
                            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
                        },
                        '&.Mui-focused': {
                            boxShadow: '0 4px 16px rgba(37, 99, 235, 0.1)',
                        },
                    },
                },
            },
        },
    },
});

// Styled Components
const PageContainer = styled(motion.div)(({theme}) => ({
    minHeight: '100vh',
    padding: theme.spacing(4),
    background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
}));

const StyledPaper = styled(Paper)(({theme}) => ({
    background: alpha(theme.palette.background.paper, 0.9),
    backdropFilter: 'blur(10px)',
    borderRadius: 24,
    padding: theme.spacing(3),
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
    border: '1px solid rgba(255, 255, 255, 0.5)',
    transition: 'transform 0.3s ease-in-out',
    '&:hover': {
        transform: 'translateY(-5px)',
    },
}));

const StyledListItem = styled(ListItem)(({theme}) => ({
    borderRadius: 12,
    marginBottom: theme.spacing(1),
    backgroundColor: alpha(theme.palette.background.paper, 0.8),
    backdropFilter: 'blur(10px)',
    border: '1px solid rgba(255, 255, 255, 0.5)',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    '&:hover': {
        transform: 'scale(1.02)',
        backgroundColor: alpha(theme.palette.primary.light, 0.1),
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
    },
}));

const StyledIconButton = styled(IconButton)(({theme}) => ({
    color: theme.palette.error.main,
    transition: 'all 0.2s ease-in-out',
    '&:hover': {
        backgroundColor: alpha(theme.palette.error.main, 0.1),
        transform: 'rotate(8deg)',
    },
}));

function PeriodManagement() {
    // Detect if global scheduler is mounted to avoid duplicate scheduling
    const isGlobalSchedulerActive = typeof window !== 'undefined' && window.__GLOBAL_PERIOD_BELL_SCHEDULER;

    const [newPeriod, setNewPeriod] = useState('');
    const [startTime, setStartTime] = useState('');
    const [endTime, setEndTime] = useState('');
    const [selectedDays, setSelectedDays] = useState({
        monday: true,
        tuesday: true,
        wednesday: true,
        thursday: true,
        friday: true,
        saturday: false,
        sunday: false,
    });
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [currentTime, setCurrentTime] = useState('');
    const [selectedBellType, setSelectedBellType] = useState('standard'); // Default bell type
    const [bellPreviewPlaying, setBellPreviewPlaying] = useState(false);
    const [nextBellTime, setNextBellTime] = useState('');
    const [nextPeriodName, setNextPeriodName] = useState('');
    const [nextPeriodType, setNextPeriodType] = useState('start'); // 'start' or 'end'
    const [selectedBellSound, setSelectedBellSound] = useState(() => {
        const stored = typeof window !== 'undefined' ? window.localStorage.getItem('selectedBellSound') : null;
        return stored || 'standard';
    });
    const [autoBellEnabled, setAutoBellEnabled] = useState(() => {
        const stored = typeof window !== 'undefined' ? window.localStorage.getItem('autoBellEnabled') : null;
        return stored === null ? true : stored === 'true';
    }); // Changed to true to enable by default
    const [bellStatusMessage, setBellStatusMessage] = useState('');
    const [bellStatusVisible, setBellStatusVisible] = useState(false);
    const [bellSoundDuration, setBellSoundDuration] = useState('');
    const [currentSeason, setCurrentSeason] = useState(() => {
        if (typeof window !== 'undefined') {
            return localStorage.getItem('currentSeason') || 'General';
        }
        return 'General';
    });
    const [selectedSeason, setSelectedSeason] = useState(currentSeason); // New state for season

    const dispatch = useDispatch();
    const periods = useSelector((state) => state?.period?.periods || []);
    const userData = useSelector(selectSchoolDetails);
    const schoolId = userData?.id;
    const session = userData?.session;
    const { playSound, stopSound, isPlaying, currentSound, currentDuration } = useBellSound();
    const timerRef = useRef(null);
    const previewTimerRef = useRef(null);

    // Helper function to convert 24h time format to 12h (AM/PM) format
    const formatTime12Hour = (time24h) => {
        if (!time24h) return '';

        const [hours, minutes] = time24h.split(':').map(Number);
        const period = hours >= 12 ? 'PM' : 'AM';
        const hours12 = hours % 12 || 12; // Convert 0 to 12 for 12 AM

        return `${hours12}:${minutes.toString().padStart(2, '0')} ${period}`;
    };

    useEffect(() => {
        if (schoolId && session) {
            dispatch(fetchPeriods(schoolId, session));
        }
    }, [schoolId, session, dispatch]);

    // Set up time tracking for bell scheduling
    useEffect(() => {
        // Update current time every minute
        const updateTime = () => {
            const now = new Date();
            const formattedTime = now.toLocaleTimeString('en-US', {
                hour: '2-digit',
                minute: '2-digit',
                hour12: true
            });
            setCurrentTime(formattedTime);
        };

        // Initial time update
        updateTime();

        // Set up interval for time updates (every minute)
        const timeInterval = setInterval(updateTime, 60000);

        return () => {
            clearInterval(timeInterval);
            if (timerRef.current) {
                clearTimeout(timerRef.current);
            }
        };
    }, []);

    // Persist preference changes to localStorage so global scheduler picks them up
    useEffect(() => {
        try { localStorage.setItem('autoBellEnabled', String(autoBellEnabled)); } catch (_) {}
    }, [autoBellEnabled]);

    useEffect(() => {
        if (selectedBellSound) {
            try { localStorage.setItem('selectedBellSound', selectedBellSound); } catch (_) {}
        }
    }, [selectedBellSound]);

    useEffect(() => {
        if (typeof window !== 'undefined') {
            localStorage.setItem('currentSeason', currentSeason);
        }
    }, [currentSeason]);

    // Schedule bell notifications when periods or auto bell setting changes
    useEffect(() => {
        if (isGlobalSchedulerActive) {
            // Let the global scheduler handle background bell ringing
            return;
        }
        // Cancel any existing timer
        if (timerRef.current) {
            clearTimeout(timerRef.current);
        }

        if (!autoBellEnabled || periods.length === 0) {
            setNextBellTime('');
            setNextPeriodName('');
            setNextPeriodType('');
            setBellStatusMessage('Bell notifications are disabled');
            setBellStatusVisible(false);
            return;
        }

        const scheduleNextBell = () => {
            const now = new Date();
            const currentDay = now.getDay(); // 0 (Sunday) to 6 (Saturday)
            const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
            const currentDayName = dayNames[currentDay];

            // Skip if current day is not selected
            if (!selectedDays[currentDayName]) {
                setBellStatusMessage(`No bells scheduled for ${currentDayName.charAt(0).toUpperCase() + currentDayName.slice(1)}`);
                setBellStatusVisible(true);

                // Find the next enabled day
                let daysToAdd = 1;
                let nextDayIndex = (currentDay + daysToAdd) % 7;

                while (daysToAdd <= 7 && !selectedDays[dayNames[nextDayIndex]]) {
                    daysToAdd++;
                    nextDayIndex = (currentDay + daysToAdd) % 7;
                }

                if (daysToAdd > 7) {
                    // No days are enabled
                    return;
                }

                // Schedule for the next enabled day's first period
                const nextEnabledDay = new Date(now);
                nextEnabledDay.setDate(now.getDate() + daysToAdd);
                nextEnabledDay.setHours(0, 0, 0, 0);

                // Find earliest period for the next day
                let earliestPeriod = null;
                let earliestTime = Infinity;

                periods.forEach(period => {
                    if (!period.startTime) return;

                    const [startHours, startMinutes] = period.startTime.split(':').map(Number);
                    const startTimeInMinutes = startHours * 60 + startMinutes;

                    if (startTimeInMinutes < earliestTime) {
                        earliestPeriod = period;
                        earliestTime = startTimeInMinutes;
                    }
                });

                if (earliestPeriod) {
                    const [nextHours, nextMinutes] = earliestPeriod.startTime.split(':').map(Number);
                    const nextDay = new Date(nextEnabledDay);
                    nextDay.setHours(nextHours, nextMinutes, 0, 0);

                    const timeUntilNextBell = nextDay.getTime() - now.getTime();

                    setNextBellTime(formatTime12Hour(earliestPeriod.startTime));
                    setNextPeriodName(earliestPeriod.name);
                    setNextPeriodType('start');

                    const nextDayName = dayNames[nextDayIndex].charAt(0).toUpperCase() + dayNames[nextDayIndex].slice(1);
                    setBellStatusMessage(`Next bell: ${nextDayName} at ${formatTime12Hour(earliestPeriod.startTime)} for ${earliestPeriod.name}`);
                    setBellStatusVisible(true);

                    timerRef.current = setTimeout(() => {
                        playSound(selectedBellSound);
                        setSnackbarMessage(`🔔 ${earliestPeriod.name} is starting now!`);
                        setSnackbarOpen(true);
                        scheduleNextBell();
                    }, timeUntilNextBell);

                    return;
                }
            }

            const currentHours = now.getHours();
            const currentMinutes = now.getMinutes();
            const currentTimeInMinutes = currentHours * 60 + currentMinutes;

            // Create a list of all bell times (start AND end) for today
            let allBellTimes = [];

            periods.forEach(period => {
                if (period.startTime) {
                    const [startHours, startMinutes] = period.startTime.split(':').map(Number);
                    const startTimeInMinutes = startHours * 60 + startMinutes;

                    if (startTimeInMinutes > currentTimeInMinutes) {
                        allBellTimes.push({
                            period,
                            timeInMinutes: startTimeInMinutes,
                            timeString: period.startTime,
                            type: 'start'
                        });
                    }
                }

                if (period.endTime) {
                    const [endHours, endMinutes] = period.endTime.split(':').map(Number);
                    const endTimeInMinutes = endHours * 60 + endMinutes;

                    if (endTimeInMinutes > currentTimeInMinutes) {
                        allBellTimes.push({
                            period,
                            timeInMinutes: endTimeInMinutes,
                            timeString: period.endTime,
                            type: 'end'
                        });
                    }
                }
            });

            // Sort all bell times by time
            allBellTimes.sort((a, b) => a.timeInMinutes - b.timeInMinutes);

            if (allBellTimes.length > 0) {
                // Get the next bell time (either start or end)
                const nextBell = allBellTimes[0];
                const nextBellDate = new Date(now);
                const [nextHours, nextMinutes] = nextBell.timeString.split(':').map(Number);
                nextBellDate.setHours(nextHours, nextMinutes, 0, 0);

                const timeUntilNextBell = nextBellDate.getTime() - now.getTime();

                setNextBellTime(formatTime12Hour(nextBell.timeString));
                setNextPeriodName(nextBell.period.name);
                setNextPeriodType(nextBell.type);

                const message = nextBell.type === 'start'
                    ? `Next bell: ${formatTime12Hour(nextBell.timeString)} - ${nextBell.period.name} starts`
                    : `Next bell: ${formatTime12Hour(nextBell.timeString)} - ${nextBell.period.name} ends`;

                setBellStatusMessage(message);
                setBellStatusVisible(true);

                // Schedule the bell
                timerRef.current = setTimeout(() => {
                    // Play bell sound
                    playSound(selectedBellSound);

                    // Show notification
                    const notificationMessage = nextBell.type === 'start'
                        ? `🔔 ${nextBell.period.name} is starting now!`
                        : `🔔 ${nextBell.period.name} has ended!`;

                    setSnackbarMessage(notificationMessage);
                    setSnackbarOpen(true);

                    // Schedule the next bell
                    scheduleNextBell();
                }, timeUntilNextBell);
            } else {
                // No more bells for today, check tomorrow
                scheduleForTomorrow();
            }
        };

        const scheduleForTomorrow = () => {
            const now = new Date();
            const tomorrowDayIndex = (now.getDay() + 1) % 7;
            const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
            const tomorrowDayName = dayNames[tomorrowDayIndex];

            // Skip if tomorrow is not a selected day
            if (!selectedDays[tomorrowDayName]) {
                // Find the next enabled day
                let daysToAdd = 1;
                let nextDayIndex = (now.getDay() + daysToAdd) % 7;

                while (daysToAdd <= 7 && !selectedDays[dayNames[nextDayIndex]]) {
                    daysToAdd++;
                    nextDayIndex = (now.getDay() + daysToAdd) % 7;
                }

                if (daysToAdd > 7) {
                    // No days are enabled
                    setBellStatusMessage('No active days selected for bells');
                    setBellStatusVisible(true);
                    return;
                }

                // Schedule for the next enabled day's first period
                const nextEnabledDay = new Date(now);
                nextEnabledDay.setDate(now.getDate() + daysToAdd);
                nextEnabledDay.setHours(0, 0, 0, 0);

                // Find earliest period for the next day
                let earliestPeriod = null;
                let earliestTime = Infinity;

                periods.forEach(period => {
                    if (!period.startTime) return;

                    const [startHours, startMinutes] = period.startTime.split(':').map(Number);
                    const startTimeInMinutes = startHours * 60 + startMinutes;

                    if (startTimeInMinutes < earliestTime) {
                        earliestPeriod = period;
                        earliestTime = startTimeInMinutes;
                    }
                });

                if (earliestPeriod) {
                    const [nextHours, nextMinutes] = earliestPeriod.startTime.split(':').map(Number);
                    const nextDay = new Date(nextEnabledDay);
                    nextDay.setHours(nextHours, nextMinutes, 0, 0);

                    const timeUntilNextBell = nextDay.getTime() - now.getTime();

                    setNextBellTime(formatTime12Hour(earliestPeriod.startTime));
                    setNextPeriodName(earliestPeriod.name);
                    setNextPeriodType('start');

                    const nextDayName = dayNames[nextDayIndex].charAt(0).toUpperCase() + dayNames[nextDayIndex].slice(1);
                    setBellStatusMessage(`Next bell: ${nextDayName} at ${formatTime12Hour(earliestPeriod.startTime)} for ${earliestPeriod.name}`);
                    setBellStatusVisible(true);

                    timerRef.current = setTimeout(() => {
                        playSound(selectedBellSound);
                        setSnackbarMessage(`🔔 ${earliestPeriod.name} is starting now!`);
                        setSnackbarOpen(true);
                        scheduleNextBell();
                    }, timeUntilNextBell);
                }

                return;
            }

            // Find the earliest period for tomorrow
            let earliestPeriod = null;
            let earliestTimeInMinutes = Infinity;

            periods.forEach(period => {
                if (!period.startTime) return;

                const [startHours, startMinutes] = period.startTime.split(':').map(Number);
                const startTimeInMinutes = startHours * 60 + startMinutes;

                if (startTimeInMinutes < earliestTimeInMinutes) {
                    earliestPeriod = period;
                    earliestTimeInMinutes = startTimeInMinutes;
                }
            });

            if (earliestPeriod) {
                const tomorrow = new Date(now);
                tomorrow.setDate(tomorrow.getDate() + 1);
                tomorrow.setHours(0, 0, 0, 0);

                const [nextHours, nextMinutes] = earliestPeriod.startTime.split(':').map(Number);
                const nextBellDate = new Date(tomorrow);
                nextBellDate.setHours(nextHours, nextMinutes, 0, 0);

                const timeUntilNextBell = nextBellDate.getTime() - now.getTime();

                setNextBellTime(formatTime12Hour(earliestPeriod.startTime));
                setNextPeriodName(earliestPeriod.name);
                setNextPeriodType('start');

                const tomorrowName = tomorrowDayName.charAt(0).toUpperCase() + tomorrowDayName.slice(1);
                setBellStatusMessage(`Next bell: ${tomorrowName} at ${formatTime12Hour(earliestPeriod.startTime)} for ${earliestPeriod.name}`);
                setBellStatusVisible(true);

                timerRef.current = setTimeout(() => {
                    // Play bell sound
                    playSound(selectedBellSound);

                    // Show notification
                    setSnackbarMessage(`🔔 ${earliestPeriod.name} is starting now!`);
                    setSnackbarOpen(true);

                    // Schedule the next bell
                    scheduleNextBell();
                }, timeUntilNextBell);
            }
        };

        if (autoBellEnabled && periods.length > 0) {
            scheduleNextBell();
        }
    }, [autoBellEnabled, periods, selectedDays, isGlobalSchedulerActive]);

    // Update the bell sound when selected bell type changes
    useEffect(() => {
        if (bellSounds && bellSounds[selectedBellType]) {
            // We don't need to manage audio element manually
            // as useBellSound handles it
            setSelectedBellSound(selectedBellType);
        }
    }, [selectedBellType]);

    // Play bell sound preview
    const playBellPreview = async () => {
        setBellPreviewPlaying(true);
        await playSound(selectedBellSound);
        setBellPreviewPlaying(false);
    };

    // Function to play bell sound
    const playBellSound = () => {
        if (!bellSounds[selectedBellSound]) {
            console.error("Selected bell sound not found:", selectedBellSound);
            return;
        }

        console.log("Playing bell sound:", selectedBellSound);

        // Using the playSound function from useBellSound hook
        playSound(selectedBellSound)
            .then(() => {
                console.log("Bell sound played successfully");
            })
            .catch(err => {
                console.error('Error playing bell sound:', err);
            });
    };

    // Toggle automatic bell notifications
    const toggleAutoBell = () => {
        setAutoBellEnabled(prev => !prev);
    };

    // Handle day selection changes
    const handleDayChange = (day) => {
        setSelectedDays(prev => ({
            ...prev,
            [day]: !prev[day]
        }));
    };

    // Play bell manually
    const playBellManually = () => {
        playBellSound();
        setSnackbarMessage("Bell rung manually!");
        setSnackbarOpen(true);
    };

    const handleAddPeriod = () => {
        if (!newPeriod.trim()) {
            setSnackbarMessage("Period name cannot be blank.");
            setSnackbarOpen(true);
            return;
        }
        if (!startTime || !endTime) {
            setSnackbarMessage("Please select both start and end times.");
            setSnackbarOpen(true);
            return;
        }

        // Check if no days are selected
        const anyDaySelected = Object.values(selectedDays).some(day => day);
        if (!anyDaySelected) {
            setSnackbarMessage("Please select at least one day for the period.");
            setSnackbarOpen(true);
            return;
        }

        const periodData = {
            name: newPeriod,
            schoolId: schoolId,
            session,
            startTime,
            endTime,
            days: selectedDays,
            bellSound: selectedBellSound,
            season: selectedSeason, // Add season to period object
        };
        dispatch(createPeriod(periodData));
        setNewPeriod('');
        setStartTime('');
        setEndTime('');
        setSelectedSeason('General'); // Reset season to General
    };

    const handleDeletePeriod = (id) => {
        const confirmDelete = window.confirm("Are you sure you want to delete this period?");
        if (confirmDelete) {
            dispatch(deletePeriod(id))
                .then(() => {
                    setSnackbarMessage("Period deleted successfully.");
                    setSnackbarOpen(true);
                })
                .catch((error) => {
                    console.error('Error deleting period:', error);
                    setSnackbarMessage("Failed to delete the period. Please try again.");
                    setSnackbarOpen(true);
                });
        }
    };

    const handleCloseSnackbar = () => {
        setSnackbarOpen(false);
    };

    return (
        <ThemeProvider theme={theme}>
            <PageContainer
                initial={{opacity: 0, y: 20}}
                animate={{opacity: 1, y: 0}}
                transition={{duration: 0.5}}
            >
                <Container maxWidth="xl">
                    <Box mb={5} display="flex" alignItems="center" gap={3}>
                        <Grow in timeout={800}>
                            <Box
                                sx={{
                                    backgroundColor: 'primary.main',
                                    borderRadius: '50%',
                                    p: 2,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}
                            >
                                <ScheduleIcon sx={{fontSize: 32, color: 'white'}}/>
                            </Box>
                        </Grow>
                        <Fade in timeout={1000}>
                            <Box>
                                <Typography
                                    variant="h4"
                                    sx={{
                                        background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)',
                                        WebkitBackgroundClip: 'text',
                                        WebkitTextFillColor: 'transparent',
                                        mb: 1
                                    }}
                                >
                                    Class Period Management
                                </Typography>
                                <Typography
                                    variant="body1"
                                    color="text.secondary"
                                    sx={{
                                        fontSize: '1.1rem',
                                        opacity: 0.8
                                    }}
                                >
                                    Manage and organize your class periods efficiently
                                </Typography>
                            </Box>
                        </Fade>
                        {currentTime && (
                            <Fade in timeout={1000}>
                                <Box ml="auto" textAlign="right">
                                    <Typography variant="h6" color="primary" fontWeight="bold">
                                        {currentTime}
                                    </Typography>
                                    <Tooltip title="Ring Bell Manually">
                                        <IconButton
                                            color="primary"
                                            onClick={playBellManually}
                                            sx={{
                                                animation: autoBellEnabled ? 'pulse 2s infinite' : 'none',
                                                '@keyframes pulse': {
                                                    '0%': { boxShadow: '0 0 0 0 rgba(37, 99, 235, 0.4)' },
                                                    '70%': { boxShadow: '0 0 0 10px rgba(37, 99, 235, 0)' },
                                                    '100%': { boxShadow: '0 0 0 0 rgba(37, 99, 235, 0)' }
                                                }
                                            }}
                                        >
                                            <BellIcon />
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                            </Fade>
                        )}
                    </Box>

                    {bellStatusVisible && (
                        <Box
                            sx={{
                                mb: 4,
                                p: 2,
                                borderRadius: 2,
                                backgroundColor: alpha(theme.palette.primary.main, 0.1),
                                color: theme.palette.primary.main,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                border: '1px solid',
                                borderColor: alpha(theme.palette.primary.main, 0.3),
                            }}
                        >
                            <AlarmIcon sx={{fontSize: 20}}/>
                            <Typography variant="body2" sx={{flex: 1}}>
                                {bellStatusMessage}
                            </Typography>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={autoBellEnabled}
                                        onChange={toggleAutoBell}
                                        color="primary"
                                        size="small"
                                    />
                                }
                                label={autoBellEnabled ? "Auto Bell ON" : "Auto Bell OFF"}
                                sx={{ m: 0 }}
                            />
                        </Box>
                    )}

                    <Grid container spacing={4}>
                        <Grid item xs={12} md={6}>
                            <Zoom in timeout={1000}>
                                <StyledPaper elevation={0}>
                                    <Typography
                                        variant="h6"
                                        gutterBottom
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1,
                                            mb: 3
                                        }}
                                    >
                                        <AddIcon color="primary"/>
                                        Add New Period
                                    </Typography>
                                    <TextField
                                        label="Period Name"
                                        value={newPeriod}
                                        onChange={(e) => setNewPeriod(e.target.value)}
                                        fullWidth
                                        variant="outlined"
                                        sx={{mb: 2}}
                                        placeholder="Enter period name..."
                                    />
                                    <Grid container spacing={2} sx={{mb: 2}}>
                                        <Grid item xs={6}>
                                            <TextField
                                                label="Start Time"
                                                type="time"
                                                value={startTime}
                                                onChange={(e) => setStartTime(e.target.value)}
                                                fullWidth
                                                variant="outlined"
                                                InputLabelProps={{
                                                    shrink: true,
                                                }}
                                                inputProps={{
                                                    step: 300, // 5 min
                                                }}
                                                helperText={startTime ? `(${formatTime12Hour(startTime)})` : ''}
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextField
                                                label="End Time"
                                                type="time"
                                                value={endTime}
                                                onChange={(e) => setEndTime(e.target.value)}
                                                fullWidth
                                                variant="outlined"
                                                InputLabelProps={{
                                                    shrink: true,
                                                }}
                                                inputProps={{
                                                    step: 300, // 5 min
                                                }}
                                                helperText={endTime ? `(${formatTime12Hour(endTime)})` : ''}
                                            />
                                        </Grid>
                                    </Grid>

                                    <FormControl component="fieldset" sx={{ mb: 2 }}>
                                        <FormLabel component="legend">Active Days</FormLabel>
                                        <FormGroup row>
                                            {Object.keys(selectedDays).map((day) => (
                                                <FormControlLabel
                                                    key={day}
                                                    control={
                                                        <Switch
                                                            checked={selectedDays[day]}
                                                            onChange={() => handleDayChange(day)}
                                                            color="primary"
                                                        />
                                                    }
                                                    label={day.charAt(0).toUpperCase() + day.slice(1)}
                                                />
                                            ))}
                                        </FormGroup>
                                    </FormControl>

                                    <FormControl fullWidth variant="outlined" sx={{ mb: 2 }}>
                                        <FormLabel>Bell Sound</FormLabel>
                                        <Select
                                            value={selectedBellSound}
                                            onChange={(e) => setSelectedBellSound(e.target.value)}
                                            label="Bell Sound"
                                            displayEmpty
                                            inputProps={{ 'aria-label': 'Without label' }}
                                            renderValue={(selected) => {
                                                if (!selected) {
                                                    return <em>Select a bell sound</em>;
                                                }

                                                const soundName = bellSounds[selected]?.name;
                                                return soundName ? soundName : selected;
                                            }}
                                        >
                                            <MenuItem value="">
                                                <em>Select a bell sound</em>
                                            </MenuItem>
                                            {Object.entries(bellSounds).map(([key, sound]) => (
                                                <MenuItem key={key} value={key}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                                                        <MusicNoteIcon sx={{ mr: 1, color: 'text.secondary' }} />
                                                        <Typography variant="body2" color="text.primary">
                                                            {sound.name}
                                                        </Typography>
                                                    </Box>
                                                </MenuItem>
                                            ))}
                                        </Select>
                                        <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
                                            {/* Sound player visualization */}
                                            {bellPreviewPlaying && (
                                                <Paper
                                                    elevation={0}
                                                    sx={{
                                                        p: 1.5,
                                                        borderRadius: 2,
                                                        backgroundColor: alpha(theme.palette.primary.light, 0.1),
                                                        border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 2
                                                    }}
                                                >
                                                    <Box sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        position: 'relative'
                                                    }}>
                                                        <MusicNoteIcon
                                                            color="primary"
                                                            sx={{
                                                                animation: 'pulse 1s infinite',
                                                                '@keyframes pulse': {
                                                                    '0%': { opacity: 1, transform: 'scale(1)' },
                                                                    '50%': { opacity: 0.6, transform: 'scale(0.95)' },
                                                                    '100%': { opacity: 1, transform: 'scale(1)' }
                                                                }
                                                            }}
                                                        />
                                                        <Box sx={{
                                                            position: 'absolute',
                                                            display: 'flex',
                                                            gap: 0.5,
                                                            ml: 4
                                                        }}>
                                                            {[0, 1, 2, 3].map((i) => (
                                                                <Box
                                                                    key={i}
                                                                    sx={{
                                                                        width: '3px',
                                                                        height: '10px',
                                                                        borderRadius: '3px',
                                                                        backgroundColor: theme.palette.primary.main,
                                                                        animation: `soundWave ${0.5 + i * 0.1}s ease-in-out infinite alternate`,
                                                                        '@keyframes soundWave': {
                                                                            '0%': { height: '5px' },
                                                                            '100%': { height: '15px' }
                                                                        }
                                                                    }}
                                                                />
                                                            ))}
                                                        </Box>
                                                    </Box>
                                                    <Box sx={{ flex: 1 }}>
                                                        <Typography variant="caption" color="primary.dark" fontWeight="medium">
                                                            Now playing: {bellSounds[selectedBellSound]?.name}
                                                        </Typography>
                                                        <Typography variant="caption" display="block" color="text.secondary">
                                                            Duration: {bellSounds[selectedBellSound]?.duration}
                                                        </Typography>
                                                    </Box>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => {
                                                            stopSound();
                                                            setBellPreviewPlaying(false);
                                                        }}
                                                        sx={{
                                                            backgroundColor: alpha(theme.palette.error.main, 0.1),
                                                            '&:hover': {
                                                                backgroundColor: alpha(theme.palette.error.main, 0.2),
                                                            }
                                                        }}
                                                    >
                                                        <Box
                                                            component="span"
                                                            sx={{
                                                                width: '10px',
                                                                height: '10px',
                                                                backgroundColor: theme.palette.error.main,
                                                                borderRadius: '2px'
                                                            }}
                                                        />
                                                    </IconButton>
                                                </Paper>
                                            )}
                                            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                                <Button
                                                    size="small"
                                                    startIcon={bellPreviewPlaying ? null : <PlayIcon />}
                                                    onClick={playBellPreview}
                                                    disabled={bellPreviewPlaying}
                                                    sx={{
                                                        opacity: bellPreviewPlaying ? 0.6 : 1
                                                    }}
                                                >
                                                    {bellPreviewPlaying ? 'Playing...' : 'Preview Sound'}
                                                </Button>
                                            </Box>
                                        </Box>
                                    </FormControl>

                                    <FormControl fullWidth variant="outlined" sx={{ mb: 2 }}>
                                        <FormLabel>Season</FormLabel>
                                        <Select
                                            value={selectedSeason}
                                            onChange={(e) => setSelectedSeason(e.target.value)}
                                            label="Season"
                                            displayEmpty
                                            inputProps={{ 'aria-label': 'Without label' }}
                                        >
                                            <MenuItem value="General"><em>General</em></MenuItem>
                                            <MenuItem value="Summer">Summer</MenuItem>
                                            <MenuItem value="Winter">Winter</MenuItem>
                                        </Select>
                                    </FormControl>

                                    <Button
                                        onClick={handleAddPeriod}
                                        variant="contained"
                                        color="primary"
                                        fullWidth
                                        startIcon={<AddIcon/>}
                                    >
                                        Add Period
                                    </Button>
                                </StyledPaper>
                            </Zoom>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Zoom in timeout={1000}>
                                <StyledPaper elevation={0}>
                                    <Typography variant="h6" fontWeight="bold" gutterBottom>
                                        Set Current Season
                                    </Typography>
                                    <FormControl fullWidth variant="outlined" sx={{ mb: 2 }}>
                                        <Select
                                            value={selectedSeason}
                                            onChange={(e) => setSelectedSeason(e.target.value)}
                                            label="Current Season"
                                            displayEmpty
                                            inputProps={{ 'aria-label': 'Set Current Season' }}
                                        >
                                            <MenuItem value="General">General</MenuItem>
                                            <MenuItem value="Summer">Summer</MenuItem>
                                            <MenuItem value="Winter">Winter</MenuItem>
                                        </Select>
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            sx={{ mt: 1 }}
                                            onClick={async () => {
                                                setCurrentSeason(selectedSeason);
                                                try {
                                                    console.log('Updating periods with:', { schoolId, session, selectedSeason });
                                                    const result = await dispatch(updatePeriods(schoolId, session, selectedSeason));
                                                    console.log('Update result:', result);
                                                    if (result && (!result.error || result.type?.endsWith('/fulfilled'))) {
                                                        dispatch(fetchPeriods(schoolId, session));
                                                    }
                                                } catch (error) {
                                                    // Optionally show error to user
                                                }
                                            }}
                                        >
                                            Update
                                        </Button>
                                        <Typography variant="caption" color="primary" sx={{ mt: 1 }}>
                                            Marked as current season
                                        </Typography>
                                    </FormControl>
                                    <Divider sx={{ my: 3, borderColor: 'primary.main', borderWidth: 2 }} />
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                        <Typography
                                            variant="h6"
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1
                                            }}
                                        >
                                            <ScheduleIcon color="primary"/>
                                            Existing Periods
                                        </Typography>
                                        {bellSoundDuration && (
                                            <Typography variant="caption" color="text.secondary">
                                                Sound duration: {bellSoundDuration}
                                            </Typography>
                                        )}
                                    </Box>
                                    <List
                                        sx={{
                                            maxHeight: 400,
                                            overflowY: 'auto',
                                            '&::-webkit-scrollbar': {
                                                width: '8px',
                                            },
                                            '&::-webkit-scrollbar-track': {
                                                background: '#f1f1f1',
                                                borderRadius: '10px',
                                            },
                                            '&::-webkit-scrollbar-thumb': {
                                                background: '#888',
                                                borderRadius: '10px',
                                                '&:hover': {
                                                    background: '#555',
                                                },
                                            },
                                        }}
                                    >
                                        {periods.length === 0 ? (
                                            <Typography color="text.secondary" align="center" sx={{ py: 3 }}>
                                                No periods added yet. Create your first period!
                                            </Typography>
                                        ) : (
                                            periods.map((period, index) => (
                                                <Fade in key={period.id} timeout={500 + index * 100}>
                                                    <StyledListItem>
                                                        <ListItemText
                                                            primary={period.name}
                                                            secondary={
                                                                <>
                                                                    {`${formatTime12Hour(period.startTime)} - ${formatTime12Hour(period.endTime)}`}
                                                                    <Typography variant="caption" display="block" color="text.secondary">
                                                                        Season: {period.season} | Current Season: {period.currentSeason}
                                                                    </Typography>
                                                                    {period.days && (
                                                                        <Typography variant="caption" display="block" color="text.secondary">
                                                                            Days: {Object.entries(period.days)
                                                                                .filter(([day, active]) => active)
                                                                                .map(([day]) => day.charAt(0).toUpperCase() + day.slice(1))
                                                                                .join(', ')}
                                                                        </Typography>
                                                                    )}
                                                                </>
                                                            }
                                                            sx={{
                                                                '& .MuiListItemText-primary': {
                                                                    fontWeight: 500,
                                                                },
                                                            }}
                                                        />
                                                        <StyledIconButton
                                                            onClick={() => handleDeletePeriod(period.id)}
                                                            size="small"
                                                        >
                                                            <DeleteIcon/>
                                                        </StyledIconButton>
                                                    </StyledListItem>
                                                </Fade>
                                            ))
                                        )}
                                    </List>
                                </StyledPaper>
                            </Zoom>
                        </Grid>
                    </Grid>


                    <Snackbar
                        open={snackbarOpen}
                        autoHideDuration={3000}
                        onClose={handleCloseSnackbar}
                        anchorOrigin={{vertical: 'top', horizontal: 'center'}}
                    >
                        <Alert
                            onClose={handleCloseSnackbar}
                            severity={snackbarMessage.includes('successfully') || snackbarMessage.includes('Bell rung') ? 'success' : 'error'}
                            sx={{
                                width: '100%',
                                borderRadius: 2,
                                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                            }}
                        >
                            {snackbarMessage}
                        </Alert>
                    </Snackbar>
                </Container>
            </PageContainer>
        </ThemeProvider>
    );
}

export default PeriodManagement;
